// src/components/topbar.js — Universal Hub (V2-Topbar.1/2/3)
// Ilha global flutuante: brand mini + busca global + toggle tema + sino.
// Overlay de busca com 5 tabs (Tudo/Obras/Clientes/Ações/IA ✨).
// CSS .uh-* / .topbar.uh-bar já vive em _canonical.css (transplante 1:1).
import { sb } from '../api/supabaseClient.js';
import { tenant } from '../context/tenantContext.js';
import { navigate } from '../router/router.js';
import { esc } from '../lib/dom.js';
import { calcPctObra } from '../lib/status.js';
import { showToast } from '../lib/toast.js';
import { aiSearch } from '../api/aiEdgeFunctions.js';
// Fuse.js carregado sob demanda (dynamic import em _cpalIndexar) - fora do boot critico.
import { uiIco } from '../lib/uiIcons.js';
import { aivaMark } from './aivaCard.js';
import { iniciarNotificacoesRealtime } from '../lib/notificacoesRealtime.js';

const PRO_PLANS = ['empresa', 'enterprise'];
function _isPlanoPRO() { return PRO_PLANS.includes(tenant.config?.plano); }

const _uhState = { tab: 'tudo', ai: null, debounce: null };
const _cpal = { obras: [], clientes: [], fornecedores: [], acoes: [], fuse: null };

// ─── markup da ilha + overlay ────────────────────────────────────────
function _islandHTML() {
  return `
  <div class="topbar uh-bar" id="uh-bar">
    <div class="uh-brand-mini" title="Angular ERP">AE</div>
    <div class="uh-sep"></div>
    <div class="global-search-wrap">
      <span class="global-search-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></span>
      <input class="global-search-input" type="text" id="global-search" placeholder="Buscar ou perguntar à IA · ⌘K" autocomplete="off">
    </div>
    <div class="uh-theme" id="uh-theme" title="Alternar tema">
      <svg id="theme-icon-sun" width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:opacity .3s"><circle cx="8" cy="8" r="3" stroke="currentColor" stroke-width="1.4"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M11.54 3.05l-1.41 1.41M3.05 11.54l1.41 1.41" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
      <div style="width:28px;height:14px;border-radius:99px;position:relative;flex-shrink:0;transition:all .3s;border:0.5px solid var(--glass-border);background:rgba(255,255,255,.04)" id="theme-toggle-btn">
        <div id="theme-knob" style="position:absolute;top:2px;left:2px;width:9px;height:9px;border-radius:50%;background:var(--text3);transition:all .3s cubic-bezier(.34,1.56,.64,1)"></div>
      </div>
      <svg id="theme-icon-moon" width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;opacity:.4;transition:opacity .3s"><path d="M13.5 10A6 6 0 016 2.5 6 6 0 1013.5 10z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
    </div>
    <div class="notif-bell" id="notif-bell" title="Notificações" style="display:flex;align-items:center;justify-content:center;cursor:pointer">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5A4.5 4.5 0 003.5 6v2.5l-1 2h11l-1-2V6A4.5 4.5 0 008 1.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M6.5 13a1.5 1.5 0 003 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
      <span id="notif-badge" class="notif-badge" style="display:none">0</span>
      <div class="notif-dropdown" id="notif-dropdown">
        <div class="notif-header">
          <span style="display:flex;align-items:center;gap:7px"><svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 1.5A4.5 4.5 0 003.5 6v2.5l-1 2h11l-1-2V6A4.5 4.5 0 008 1.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M6.5 13a1.5 1.5 0 003 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg> Notificações</span>
          <span id="notif-mark-all" title="Marcar todas como lidas" style="font-size:11px;color:var(--sc-info);cursor:pointer;white-space:nowrap">✓ Lidas</span>
        </div>
        <div id="notif-list"><div class="notif-empty">Carregando...</div></div>
      </div>
    </div>
  </div>
  <div class="uh-overlay" id="uh-overlay">
    <div class="uh-pal-tabs" id="uh-pal-tabs">
      <button class="uh-pal-tab active" data-tab="tudo">Tudo</button>
      <button class="uh-pal-tab" data-tab="obras">Obras <span class="cnt" id="uh-cnt-obras">0</span></button>
      <button class="uh-pal-tab" data-tab="clientes">Clientes <span class="cnt" id="uh-cnt-clientes">0</span></button>
      <button class="uh-pal-tab" data-tab="acoes">Ações <span class="cnt" id="uh-cnt-acoes">0</span></button>
      <button class="uh-pal-tab is-ia" data-tab="ia">IA ✨</button>
    </div>
    <div class="uh-pal-body" id="uh-pal-body"></div>
    <div class="uh-pal-foot">
      <div class="l"><kbd>↑↓</kbd> navegar <kbd>↵</kbd> abrir</div>
      <div class="r"><kbd>ESC</kbd> fechar</div>
    </div>
  </div>`;
}

// ─── mount ───────────────────────────────────────────────────────────
export function mountTopbar() {
  const wrap = document.querySelector('.app-main-wrap');
  if (!wrap || document.getElementById('uh-bar')) return;
  wrap.insertAdjacentHTML('afterbegin', _islandHTML());
  _bindIsland();
  _applyThemeKnob();
  _bindUhHooks();
  _cpalIndexar().catch((e) => console.warn('[topbar] index:', e));
  _carregarNotificacoes().catch(() => {});
  iniciarNotificacoesRealtime(() => _carregarNotificacoes().catch(() => {}));
}

// ─── tema ────────────────────────────────────────────────────────────
function _toggleTheme() {
  const isLight = document.documentElement.classList.toggle('light');
  try { localStorage.setItem('angular-theme', isLight ? 'light' : 'dark'); } catch (_) {}
  _applyThemeKnob();
  showToast(isLight ? 'Tema claro ativado' : 'Tema escuro ativado');
}
function _applyThemeKnob() {
  const isLight = document.documentElement.classList.contains('light');
  const knob = document.getElementById('theme-knob');
  const sun = document.getElementById('theme-icon-sun');
  const moon = document.getElementById('theme-icon-moon');
  const btn = document.getElementById('theme-toggle-btn');
  if (knob) { knob.style.left = isLight ? '2px' : '17px'; knob.style.background = isLight ? 'var(--sc-warn)' : 'var(--sc-info)'; }
  if (btn) btn.style.background = isLight ? 'color-mix(in oklab, var(--sc-warn) 18%, transparent)' : 'color-mix(in oklab, var(--sc-info) 16%, transparent)';
  if (sun) sun.style.opacity = isLight ? '1' : '.4';
  if (moon) moon.style.opacity = isLight ? '.4' : '1';
}

// ─── bindings da ilha ────────────────────────────────────────────────
function _bindIsland() {
  document.getElementById('uh-theme')?.addEventListener('click', _toggleTheme);
  document.querySelectorAll('#uh-pal-tabs .uh-pal-tab').forEach((t) => {
    t.addEventListener('click', () => _uhSetTab(t.dataset.tab));
  });
  document.getElementById('uh-pal-body')?.addEventListener('click', _onBodyClick);
  // sino
  const bell = document.getElementById('notif-bell');
  bell?.addEventListener('click', (e) => {
    if (e.target.closest('#notif-mark-all')) { e.stopPropagation(); _marcarTodasLidas(); return; }
    if (e.target.closest('.notif-dropdown') && !e.target.closest('[data-notif-id]')) return;
    const item = e.target.closest('[data-notif-id]');
    if (item) { _abrirNotificacao(item.dataset.notifId, item.dataset.notifLink, item.dataset.notifLinkid); return; }
    document.getElementById('notif-dropdown')?.classList.toggle('open');
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#notif-bell')) document.getElementById('notif-dropdown')?.classList.remove('open');
  });
}

// ─── overlay busca ───────────────────────────────────────────────────
function _uhOverlay() { return document.getElementById('uh-overlay'); }
function _uhExpand() {
  const ov = _uhOverlay(); if (!ov || ov.classList.contains('is-open')) return;
  ov.classList.add('is-open'); document.body.classList.add('uh-pal-open');
  _uhRenderTab(_uhState.tab); _uhUpdateContadores();
}
function _uhCollapse() {
  const ov = _uhOverlay(); if (!ov) return;
  ov.classList.remove('is-open'); document.body.classList.remove('uh-pal-open');
}
function _uhSetTab(tab) {
  _uhState.tab = tab;
  document.querySelectorAll('#uh-pal-tabs .uh-pal-tab').forEach((t) => t.classList.toggle('active', t.dataset.tab === tab));
  _uhRenderTab(tab);
}
function _uhQuery() { const i = document.getElementById('global-search'); return i ? (i.value || '').trim() : ''; }

function _uhUpdateContadores() {
  const q = _uhQuery();
  let co, cc, ca;
  if (q && _cpal.fuse) {
    const m = _cpal.fuse.search(q).slice(0, 200).map((r) => r.item);
    co = m.filter((i) => i._tipo === 'obra').length;
    cc = m.filter((i) => i._tipo === 'cliente' || i._tipo === 'fornecedor').length;
    ca = m.filter((i) => i._tipo === 'acao').length;
  } else {
    co = _cpal.obras.length; cc = _cpal.clientes.length + _cpal.fornecedores.length; ca = _cpal.acoes.length;
  }
  const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  set('uh-cnt-obras', co); set('uh-cnt-clientes', cc); set('uh-cnt-acoes', ca);
}

function _uhBuscaPorTipo(tipos, limit) {
  const q = _uhQuery();
  let all;
  if (q && _cpal.fuse) all = _cpal.fuse.search(q).slice(0, 100).map((r) => r.item);
  else all = [].concat(_cpal.obras, _cpal.clientes, _cpal.fornecedores, _cpal.acoes);
  return all.filter((i) => tipos.indexOf(i._tipo) >= 0).slice(0, limit || 12);
}

function _rowObra(o) {
  return `<div class="uh-row" data-nav="obra" data-id="${esc(o.id)}">
    <div class="ic">${uiIco('home', 14)}</div>
    <div class="meta"><div class="t">${esc(o.nome || '—')}</div><div class="s">${esc(o.cliente || '')}${o.cidade ? ' · ' + esc(o.cidade) : ''}</div></div>
    <span class="pill">${esc(o.status || '')}</span></div>`;
}
function _rowCliFor(c) {
  const isFor = c._tipo === 'fornecedor';
  return `<div class="uh-row" data-nav="${isFor ? 'fornecedor' : 'cliente'}" data-id="${esc(c.id)}">
    <div class="ic">${uiIco(isFor ? 'box' : 'user', 14)}</div>
    <div class="meta"><div class="t">${esc(c.nome || '—')}</div><div class="s">${esc(c.doc || '')}</div></div>
    <span class="pill">${isFor ? 'Fornecedor' : 'Cliente'}</span></div>`;
}
function _rowAcao(a) {
  return `<div class="uh-row" data-nav="acao" data-navegar="${esc(a.navegar || '')}">
    <div class="ic">${uiIco('bolt', 14)}</div>
    <div class="meta"><div class="t">${esc(a.nome || '—')}</div><div class="s">${esc(a.grupo || a.atalho || 'Ação rápida')}</div></div>
    ${a.atalho ? `<span class="pill">${esc(a.atalho)}</span>` : ''}</div>`;
}
function _uhEmpty(t) {
  return `<div class="uh-empty"><div class="em-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></div>${esc(t)}</div>`;
}

function _uhRenderTab(tab) {
  const body = document.getElementById('uh-pal-body'); if (!body) return;
  const q = _uhQuery();
  if (tab === 'ia') {
    body.innerHTML = _renderTabIA();
    setTimeout(() => document.getElementById('uh-ai-input')?.focus(), 100);
    return;
  }
  if (tab === 'tudo') {
    const obras = _uhBuscaPorTipo(['obra'], 5), clis = _uhBuscaPorTipo(['cliente', 'fornecedor'], 5), acoes = _uhBuscaPorTipo(['acao'], 5);
    let html = '';
    if (obras.length) html += '<div class="uh-grp">Obras</div>' + obras.map(_rowObra).join('');
    if (clis.length) html += '<div class="uh-grp">Clientes &amp; Fornecedores</div>' + clis.map(_rowCliFor).join('');
    if (acoes.length) html += '<div class="uh-grp">Ações rápidas</div>' + acoes.map(_rowAcao).join('');
    body.innerHTML = html || _uhEmpty(q ? `Nenhum resultado para "${esc(q)}"` : 'Comece digitando para buscar obras, clientes, ações…');
    return;
  }
  if (tab === 'obras') {
    const o = _uhBuscaPorTipo(['obra'], 24);
    body.innerHTML = o.length ? `<div class="uh-grp">${o.length} obra(s)</div>` + o.map(_rowObra).join('') : _uhEmpty('Nenhuma obra encontrada.');
    return;
  }
  if (tab === 'clientes') {
    const c = _uhBuscaPorTipo(['cliente', 'fornecedor'], 24);
    body.innerHTML = c.length ? `<div class="uh-grp">${c.length} contato(s)</div>` + c.map(_rowCliFor).join('') : _uhEmpty('Nenhum cliente ou fornecedor encontrado.');
    return;
  }
  if (tab === 'acoes') {
    const a = _uhBuscaPorTipo(['acao'], 24);
    body.innerHTML = a.length ? `<div class="uh-grp">${a.length} ação(ões)</div>` + a.map(_rowAcao).join('') : _uhEmpty('Nenhuma ação rápida encontrada.');
  }
}

function _renderTabIA() {
  if (!_isPlanoPRO()) {
    return `<div class="uh-upsell">
      <div class="crown"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.5 5.5L20 10l-5.5 2.5L12 18l-2.5-5.5L4 10l5.5-2.5z"/></svg></div>
      <div class="pro-pill">✨ Plano PRO</div>
      <h4>Busca com a AIVA</h4>
      <p>Pergunte em linguagem natural sobre suas obras, clientes, finanças e a IA responde com sugestões acionáveis baseadas no seu contexto.</p>
      <ul>
        <li>Pergunte: <em>"quais obras estão atrasadas?"</em>, <em>"resumir RDOs da semana"</em>, <em>"quanto entra no caixa em junho?"</em></li>
        <li>Sugestões inteligentes com ações sugeridas (1 clique pra navegar)</li>
        <li>50 buscas IA por dia · cache de 5min · LGPD-safe</li>
        <li>Continue usando <kbd>⌘K</kbd> para busca rápida em obras/clientes/ações</li>
      </ul>
      <button class="cta" id="uh-upsell-cta">Conhecer o Plano PRO →</button>
      <a class="secondary-link" id="uh-upsell-close">Fechar e continuar no plano atual</a>
    </div>`;
  }
  const st = _uhState.ai || {};
  const inputHTML = `<div class="uh-ai-input-wrap">
      <div class="ai-ic ai-ic-aiva">${aivaMark(28)}</div>
      <input class="uh-ai-input" id="uh-ai-input" placeholder="Pergunte à AIVA sobre seu ERP..." />
      <button class="uh-ai-submit" id="uh-ai-submit-btn">Perguntar</button>
    </div>`;
  let bodyHTML = '';
  if (st.loading) {
    bodyHTML = '<div class="uh-ai-loading"><div class="spin"></div>A AIVA está consultando...</div>';
  } else if (st.error) {
    bodyHTML = `<div class="uh-ai-resp"><div class="chip-status err">⚠ ${esc(st.error_chip || 'Erro')}</div>${esc(st.error_msg || 'Não foi possível processar.')}</div>`;
  } else if (st.resposta) {
    const chip = '<div class="chip-status live">✦ by AIVA</div>';
    bodyHTML = `<div class="uh-ai-resp">${chip}<div>${_md(st.resposta)}</div></div>`;
    if (st.acoes && st.acoes.length) {
      bodyHTML += '<div class="uh-ai-acoes-title">Ações sugeridas</div>';
      st.acoes.forEach((a) => {
        bodyHTML += `<div class="uh-ai-acao" data-nav="acao" data-navegar="${esc(a.navegar || '')}">
            <div class="ai-act-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 12h14M13 5l7 7-7 7"/></svg></div>
            <div class="ai-act-meta"><div class="t">${esc(a.titulo)}</div><div class="s">${esc(a.motivo)}</div></div>
            <div class="arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg></div>
          </div>`;
      });
    }
  } else {
    bodyHTML = `<div class="uh-empty">
      Pergunte sobre suas obras, clientes, finanças, RH...<br>
      <span style="font-size:11.5px;opacity:.7">Ex: "qual obra tem mais inadimplência?" · "resumir últimos RDOs"</span></div>`;
  }
  return '<div class="uh-ai-wrap">' + inputHTML + bodyHTML + '</div>';
}

function _md(s) { return esc(s).replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>'); }

// clicks no corpo do overlay (delegação)
function _onBodyClick(e) {
  if (e.target.closest('#uh-upsell-cta')) { showToast('Entre em contato com o suporte para liberar o Plano PRO!', 'info'); return; }
  if (e.target.closest('#uh-upsell-close')) { _uhCollapse(); return; }
  if (e.target.closest('#uh-ai-submit-btn')) { _uhAiSubmit(); return; }
  const row = e.target.closest('[data-nav]');
  if (row) { _uhNavegar({ tipo: row.dataset.nav, id: row.dataset.id, navegar: row.dataset.navegar }); return; }
}

async function _uhAiSubmit() {
  const inp = document.getElementById('uh-ai-input');
  const query = inp ? (inp.value || '').trim() : '';
  if (!query) { inp?.focus(); return; }
  _uhState.ai = { loading: true }; _uhRenderTab('ia');
  try {
    const ctx = await _uhBuildContext();
    const data = await aiSearch(query, ctx);
    _uhState.ai = { resposta: data.resposta || '', acoes: data.acoes || [], cached: !!data.cached };
  } catch (err) {
    let chip = 'Erro';
    if (err.status === 402) chip = 'PRO necessário';
    else if (err.status === 429) chip = 'Limite diário';
    else if (err.status === 502) chip = 'IA indisponível';
    else if (err.status) chip = 'Erro ' + err.status;
    _uhState.ai = { error: true, error_chip: chip, error_msg: err.body?.message || err.message || 'Não foi possível processar a busca IA.' };
  }
  _uhRenderTab('ia');
}

async function _uhBuildContext() {
  const obrasIds = _cpal.obras.slice(0, 20).map((o) => o.id).filter(Boolean);
  let obrasRicas = [];
  if (obrasIds.length) {
    try {
      const r = await sb.from('obras').select('id,nome,codigo,status,valor_contrato,data_previsao_fim,etapas_obra(progresso,peso,valor_etapa)').in('id', obrasIds);
      obrasRicas = (r.data || []).map((o) => {
        let pct = 0;
        if (o.etapas_obra && o.etapas_obra.length) {
          pct = calcPctObra(o.etapas_obra);
        } else if (o.status === 'concluida') pct = 100;
        return { codigo: o.codigo || '', nome: o.nome, status: o.status, pct_executado: pct, valor_contrato: o.valor_contrato, data_previsao_fim: o.data_previsao_fim };
      });
    } catch (e) { console.warn('uh-ctx obras', e); }
  }
  const clientes = _cpal.clientes.slice(0, 15).map((c) => {
    const nm = (c.nome || '').trim(); const partes = nm.split(/\s+/);
    return { nome: partes.length > 1 ? partes[0] + ' ' + partes.slice(1).map((p) => (p[0] || '').toUpperCase() + '.').join('') : nm };
  });
  let kpis = {};
  try {
    const hoje = new Date().toISOString().slice(0, 10);
    const [a, b, c] = await Promise.all([
      sb.from('obras').select('*', { count: 'exact', head: true }).in('status', ['em_andamento', 'planejamento']),
      sb.from('contas_receber').select('*', { count: 'exact', head: true }).lt('data_vencimento', hoje).neq('status', 'recebido'),
      sb.from('tarefas').select('*', { count: 'exact', head: true }).lt('data_prazo', hoje).neq('status', 'concluida'),
    ]);
    kpis = { obras_ativas: a.count || 0, cr_vencidas: b.count || 0, tarefas_atrasadas: c.count || 0 };
  } catch (e) { console.warn('uh-ctx kpis', e); }
  return {
    obras_ativas: obrasRicas, clientes_principais: clientes, kpis,
    acoes_disponiveis: ['obras', 'clientes', 'fornecedores', 'financeiro', 'contas_receber', 'contas_pagar', 'tarefas', 'rdo', 'despesas', 'compras', 'rh', 'dashboard', 'bi', 'mapa', 'relatorios', 'garantias'],
  };
}

function _uhNavegar(item) {
  _uhCollapse();
  if (!item) return;
  try {
    if (item.tipo === 'obra' && item.id) return navigate('obra', { id: item.id });
    if (item.tipo === 'cliente' && item.id) return navigate('clientes');
    if (item.tipo === 'fornecedor' && item.id) return navigate('fornecedores');
    if (item.tipo === 'acao' && item.navegar) return navigate(item.navegar);
  } catch (e) { console.warn('[uh-navegar]', e, item); }
}

// ─── índice fuse (obras/clientes/fornecedores/ações) ─────────────────
async function _cpalIndexar() {
  if (!tenant.empresaId) return;
  const [{ data: obras }, { data: cli }, { data: forn }] = await Promise.all([
    sb.from('obras').select('id,nome,status,endereco,clientes(nome_razao_social)').neq('status', 'cancelada').limit(200),
    sb.from('clientes').select('id,nome_razao_social,cpf_cnpj').limit(300),
    sb.from('fornecedores').select('id,razao_social,nome_fantasia,cpf_cnpj').limit(200),
  ]);
  _cpal.obras = (obras || []).map((o) => ({
    id: o.id, nome: o.nome, cliente: o.clientes?.nome_razao_social || '',
    cidade: (o.endereco || '').split(',').slice(-1)[0]?.trim() || '', status: o.status || '',
    tipo: 'obra', _tipo: 'obra', _searchable: `${o.nome} ${o.clientes?.nome_razao_social || ''} ${o.endereco || ''}`,
  }));
  _cpal.clientes = (cli || []).map((c) => ({
    id: c.id, nome: c.nome_razao_social, doc: c.cpf_cnpj || '',
    tipo: 'cliente', _tipo: 'cliente', _searchable: `${c.nome_razao_social} ${c.cpf_cnpj || ''}`,
  }));
  _cpal.fornecedores = (forn || []).map((f) => ({
    id: f.id, nome: f.razao_social || f.nome_fantasia || '—', doc: f.cpf_cnpj || '',
    tipo: 'fornecedor', _tipo: 'fornecedor', _searchable: `${f.razao_social || f.nome_fantasia || ''} ${f.cpf_cnpj || ''}`,
  }));
  // FUSÃO launcher × Busca IA (04/07): Ações = TODOS os módulos dos NAV_GROUPS,
  // com as mesmas permissões da sidebar/ilha. Imports dinâmicos (sidebar importa
  // topbar — evita ciclo na inicialização).
  const [{ NAV_GROUPS_ADMIN }, { filterNavGroups }] = await Promise.all([
    import('./sidebar.js'), import('../lib/permissions.js'),
  ]);
  _cpal.acoes = filterNavGroups(NAV_GROUPS_ADMIN).flatMap((g) =>
    g.items.map((it) => ({ nome: it.label, grupo: g.label, navegar: it.page, atalho: '' }))
  ).map((a) => ({ ...a, tipo: 'acao', _tipo: 'acao', _searchable: a.nome + ' ' + a.grupo }));
  const all = [].concat(_cpal.obras, _cpal.clientes, _cpal.fornecedores, _cpal.acoes);
  const { default: Fuse } = await import('fuse.js');
  _cpal.fuse = new Fuse(all, { keys: ['_searchable'], threshold: 0.35, ignoreLocation: true, minMatchCharLength: 1 });
  _uhUpdateContadores();
}

// ─── hooks no input ──────────────────────────────────────────────────
function _bindUhHooks() {
  const inp = document.getElementById('global-search');
  const ov = _uhOverlay();
  if (!inp || !ov) return;
  inp.addEventListener('focus', _uhExpand);
  inp.addEventListener('input', () => {
    if (_uhState.debounce) clearTimeout(_uhState.debounce);
    _uhState.debounce = setTimeout(() => {
      _uhUpdateContadores();
      if (_uhState.tab !== 'ia') _uhRenderTab(_uhState.tab);
    }, 180);
  });
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); inp.focus(); _uhExpand(); return; }
    if (e.key === 'Escape' && ov.classList.contains('is-open')) { e.preventDefault(); _uhCollapse(); inp.blur(); }
    if (e.key === 'Enter' && _uhState.tab === 'ia' && document.activeElement?.id === 'uh-ai-input') { e.preventDefault(); _uhAiSubmit(); }
  });
  document.addEventListener('click', (e) => {
    if (!ov.classList.contains('is-open')) return;
    if (e.target.closest('#uh-overlay')) return;
    if (e.target.closest('.global-search-wrap')) return;
    _uhCollapse();
  });
}

// ─── abertura programática do hub (ilha ⊞ / "Ver tudo" do popover) ───
export function openBuscaHub(query) {
  const inp = document.getElementById('global-search');
  if (!inp) return;
  if (typeof query === 'string') {
    inp.value = query;
    inp.dispatchEvent(new Event('input', { bubbles: true }));
  }
  // adia 1 tick: o clique que chamou isto ainda vai borbulhar pro handler
  // global de "clique fora" do hub — expandir depois dele garante que abre.
  setTimeout(() => { inp.focus(); _uhExpand(); }, 0);
}

// ─── sino de notificações ────────────────────────────────────────────
async function _carregarNotificacoes() {
  if (!tenant.user?.id) return;
  const { data } = await sb.from('notificacoes').select('id,titulo,mensagem,lida,created_at,link_pagina,link_id')
    .eq('usuario_id', tenant.user.id).order('created_at', { ascending: false }).limit(20);
  const lista = data || [];
  const naoLidas = lista.filter((n) => !n.lida).length;
  const badge = document.getElementById('notif-badge');
  if (badge) { badge.textContent = naoLidas; badge.style.display = naoLidas ? 'flex' : 'none'; }
  const box = document.getElementById('notif-list');
  if (!box) return;
  box.innerHTML = lista.length ? lista.map((n) => `
    <div class="notif-item ${n.lida ? '' : 'unread'}" data-notif-id="${esc(n.id)}" data-notif-link="${esc(n.link_pagina || '')}" data-notif-linkid="${esc(n.link_id || '')}" style="cursor:pointer">
      <div class="notif-title">${esc(n.titulo || 'Notificação')}</div>
      ${n.mensagem ? `<div class="notif-msg">${esc(n.mensagem)}</div>` : ''}
      <div class="notif-time">${_relTime(n.created_at)}</div>
    </div>`).join('') : '<div class="notif-empty">Nenhuma notificação</div>';
}
// Slugs de link_pagina gravados em notificações que divergem do nome canônico
// da rota registrada no router. Mantém os guards de plano/permissão funcionando
// (eles são chaveados pelo nome canônico, com underscore).
const _ROTA_ALIAS = { 'nfse-tomadas': 'nfse_tomadas' };

async function _abrirNotificacao(id, link, linkId) {
  try { await sb.from('notificacoes').update({ lida: true }).eq('id', id); } catch (_) {}
  document.getElementById('notif-dropdown')?.classList.remove('open');
  _carregarNotificacoes().catch(() => {});
  const rota = _ROTA_ALIAS[link] || link;
  if (rota) navigate(rota, linkId ? { id: linkId } : {});
}
async function _marcarTodasLidas() {
  if (!tenant.user?.id) return;
  try { await sb.from('notificacoes').update({ lida: true }).eq('usuario_id', tenant.user.id).eq('lida', false); } catch (_) {}
  _carregarNotificacoes().catch(() => {});
  showToast('Notificações marcadas como lidas');
}
function _relTime(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'agora'; if (d < 3600) return Math.floor(d / 60) + 'min';
  if (d < 86400) return Math.floor(d / 3600) + 'h'; return Math.floor(d / 86400) + 'd';
}
